import React from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { ApplyGuestBrandedTheme, GuestBrandedThemeWrapper } from 'common/compounds/GuestBrandedTheme';
import { GuestBrandedDefaultColors, GuestBrandedDefaultFonts } from '../constants';

const BannerContainer = styled.div`
    width: 100%;
    max-width: 35rem;
    border-radius: 0.625rem;
    padding: 1rem 1.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    text-align: left;
    background: var(--guest-branded-background, ${GuestBrandedDefaultColors.background});
    color: var(--guest-branded-text, ${GuestBrandedDefaultColors.text});
    font-family: var(--guest-branded-font-body, ${GuestBrandedDefaultFonts.body});
`;

const Title = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 2rem;
    font-family: var(--guest-branded-font-heading, ${GuestBrandedDefaultFonts.heading});

    margin-right: 48px; /* to ensure space for the close button */
`;

const Description = styled.p`
    font-size: 0.875rem;
    font-weight: normal;
    line-height: 1.25rem;
    margin-bottom: 1rem;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Label = styled.label`
    font-size: 0.875rem;
    font-weight: normal;
    line-height: 1.25rem;
`;

const Input = styled.input`
    width: 100%;
    height: 2.5rem;
    border-radius: 6px;
    border: 1px solid var(--guest-branded-border, ${GuestBrandedDefaultColors.border});
    padding-inline: 16px;
    font-size: 1rem;
    font-weight: normal;
    line-height: 1.5;
    color: var(--guest-branded-text, ${GuestBrandedDefaultColors.text});
    background-color: transparent;

    &:focus {
        outline: none;
        border: 2px solid var(--guest-branded-action, ${GuestBrandedDefaultColors.action});
        box-shadow: 0 0 0 2px var(--guest-branded-action, ${GuestBrandedDefaultColors.action});
    }
`;

const Actions = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    flex-wrap: wrap-reverse;
    gap: 0.5rem;
    margin-top: 1rem;
`;

export const StyledButton = styled.button`
    font-size: 1rem;
    line-height: 1.5;
    font-family: var(--guest-branded-font-button, ${GuestBrandedDefaultFonts.button});
    padding: 0.5rem 1.5rem;
    border-radius: 0.625rem;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px var(--guest-branded-action, ${GuestBrandedDefaultColors.action});
    }

    &:disabled {
        cursor: default;
        opacity: 0.4;
    }
`;

const ConfirmButton = styled(StyledButton)`
    color: var(--guest-branded-actionText, ${GuestBrandedDefaultColors.actionText});
    background-color: var(--guest-branded-action, ${GuestBrandedDefaultColors.action});
    border: 1px solid transparent;

    &:hover {
        background-color: var(--guest-branded-actionHover, ${GuestBrandedDefaultColors.actionHover});
    }
`;

const CancelButton = styled(StyledButton)`
    color: var(--guest-branded-action, ${GuestBrandedDefaultColors.action});
    background: transparent;
    border: 1px solid var(--guest-branded-action, ${GuestBrandedDefaultColors.action});

    &:hover {
        background-color: var(--guest-branded-action, ${GuestBrandedDefaultColors.action});
        color: var(--guest-branded-actionText, ${GuestBrandedDefaultColors.actionText});
    }
`;

const DoNSBanner = ({
    bannerHeadline,
    bannerDescription,
    formFnameLabel,
    formLnameLabel,
    formEmailLabel,
    btnCancelText,
    btnConfirmText,
    onCancel,
    onConfirm,
    titleId,
    descriptionId,
    appearance,
}) => {
    const { register, handleSubmit } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
        },
    });

    const firstNameId = React.useId();
    const lastNameId = React.useId();
    const emailId = React.useId();

    return (
        <GuestBrandedThemeWrapper>
            <ApplyGuestBrandedTheme appearance={appearance} />
            <BannerContainer>
                <Title id={titleId}>{bannerHeadline}</Title>
                <Description id={descriptionId}>{bannerDescription}</Description>

                <Form>
                    <Field>
                        <Label htmlFor={firstNameId}>{formFnameLabel}</Label>
                        <Input id={firstNameId} type="text" {...register('firstName')} />
                    </Field>

                    <Field>
                        <Label htmlFor={lastNameId}>{formLnameLabel}</Label>
                        <Input id={lastNameId} type="text" {...register('lastName')} />
                    </Field>

                    <Field>
                        <Label htmlFor={emailId}>{formEmailLabel}</Label>
                        <Input id={emailId} type="email" {...register('email')} />
                    </Field>

                    <Actions>
                        <CancelButton type="button" onClick={onCancel}>
                            {btnCancelText}
                        </CancelButton>
                        <ConfirmButton type="button" onClick={handleSubmit((values) => onConfirm(values))}>
                            {btnConfirmText}
                        </ConfirmButton>
                    </Actions>
                </Form>
            </BannerContainer>
        </GuestBrandedThemeWrapper>
    );
};

DoNSBanner.propTypes = {
    bannerHeadline: PropTypes.string.isRequired,
    bannerDescription: PropTypes.string.isRequired,
    formFnameLabel: PropTypes.string.isRequired,
    formLnameLabel: PropTypes.string.isRequired,
    formEmailLabel: PropTypes.string.isRequired,
    btnCancelText: PropTypes.string.isRequired,
    btnConfirmText: PropTypes.string.isRequired,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    titleId: PropTypes.string,
    descriptionId: PropTypes.string,
    appearance: PropTypes.oneOf(['light', 'dark']),
};

DoNSBanner.defaultProps = {
    onCancel: () => {},
    onConfirm: () => {},
    titleId: undefined,
    descriptionId: undefined,
    appearance: 'light',
};

export default DoNSBanner;
